/**
 * The "Add" row is draggable, so its position is persisted per section on the quote as
 * `quote.options.addIndex[section]`. A value below 0 (or `undefined`) means "always last",
 * which is the default for a new quote.
 */

/** A stored addIndex below 0, or none at all, pins the add button to the bottom. */
function isBottomAnchored(addIndex: number): boolean {
  return addIndex === undefined || addIndex === null || addIndex < 0
}

/**
 * Returns the row the add button renders on. A bottom-anchored value, and a stale value past
 * the end of the list, both render last.
 */
export function sanitizeAddIndex(addIndex: number, numItems: number): number {
  return isBottomAnchored(addIndex) || addIndex > numItems ? numItems : addIndex
}

/**
 * Returns the key the new item is inserted before, for an add button on the given row.
 *
 * The button renders above `items[sanitizeAddIndex(...)]`, so that item is the anchor. A
 * button on the last row has no item below it, so `null` appends.
 */
export function resolveAddAnchorKey(items: { key?: string }[], addIndex: number): string | null {
  return items[sanitizeAddIndex(addIndex, items.length)]?.key ?? null
}

/**
 * Returns the addIndex to persist after an *item* (not the add button) is dragged.
 *
 * An add button anchored to the bottom stays anchored to the bottom. Before #23892 this
 * returned a fixed index as soon as an item was dropped into the last slot, which pinned
 * the button part way up the list. Every later item was then inserted next to that pinned
 * button, so the button and each new item drifted toward the top as the quote grew.
 *
 * `oldIndex` and `newIndex` are row indices, so they count the add button as a row.
 */
export function resolveAddIndexAfterItemMove(args: {
  addIndex: number
  numItems: number
  oldIndex: number
  newIndex: number
}): number {
  const { addIndex, numItems, oldIndex, newIndex } = args

  // Anchored to the bottom - keep it there.
  if (isBottomAnchored(addIndex)) {
    return -1
  }

  const currentRow = sanitizeAddIndex(addIndex, numItems)

  let nextRow = currentRow
  if (oldIndex < currentRow && newIndex >= currentRow) {
    nextRow = currentRow - 1
  } else if (oldIndex > currentRow && newIndex <= currentRow) {
    nextRow = currentRow + 1
  }

  // The button ended up after every item, so return it to the bottom anchor.
  return nextRow >= numItems ? -1 : nextRow
}
